import { z } from 'zod';
import { USER_ACCESS_NOTE_MAX } from '../../src/domain/access/userAccess';
import { IdSchema, InstantSchema, PermissionSchema, RowVersionSchema, ViewKeySchema } from './common.schema';

/**
 * Access per person, on top of access per role. Request of 13 September 2026,
 * section 2.
 *
 * The exceptions one person holds: permissions and screens granted beyond the
 * role, and ones taken away although the role has them. Deny wins. The role
 * matrix stays the rule for everybody; these are the exceptions to it, and the
 * server resolves both into `session.permissions` and `session.views`, so the
 * frontend reads a session exactly as it did before.
 *
 * A person with no exceptions has no record, and `GET /users/{id}/access` answers
 * for them with four empty lists, a null note and a rowVersion to send back.
 */
export const UserAccessSchema = z.object({
  userId: IdSchema,
  /** On top of the role. */
  grantedPermissions: z.array(PermissionSchema),
  grantedViews: z.array(ViewKeySchema),
  /** Taken away, even though the role has them. Deny wins. */
  deniedPermissions: z.array(PermissionSchema),
  deniedViews: z.array(ViewKeySchema),
  /** Free text, why this exception exists. Shown on the screen and in the log. */
  note: z.string().max(USER_ACCESS_NOTE_MAX).nullable(),
  updatedByUserName: z.string().nullable(),
  updatedAt: InstantSchema.nullable(),
  rowVersion: RowVersionSchema,
});

/**
 * `PUT /users/{id}/access`, with If-Match. A full replacement, for the reason
 * `PUT /access-matrix` is one: two managers editing one person at once get a 409
 * they can read instead of one silently winning. Four empty lists remove the
 * record.
 */
export const SaveUserAccessSchema = UserAccessSchema.pick({
  grantedPermissions: true,
  grantedViews: true,
  deniedPermissions: true,
  deniedViews: true,
  note: true,
});

export type UserAccess = z.infer<typeof UserAccessSchema>;
export type SaveUserAccessCommand = z.infer<typeof SaveUserAccessSchema>;
